import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, useEffect } from 'react';
import './App.css';
import Layout from './components/Layout';
import ScrollToTop from './components/ScrollToTop';
import CookieConsent from './components/CookieConsent';
import { initializeConsent } from './utils/consentManager';
// import ConsentDebug from './components/ConsentDebug';

// Lazy load all routes
const Home = lazy(() => import('./pages/Home'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const Tools = lazy(() => import('./pages/Tools'));
const Contact = lazy(() => import('./pages/Contact'));
const Custom404 = lazy(() => import('./pages/404'));
const ProductDetails = lazy(() => import('./pages/ProductDetails'));
const PricingPage = lazy(() => import('./pages/Pricing'));
const TermsScheduler = lazy(() => import('./pages/docs/TermsScheduler'));
const PrivacyScheduler = lazy(() => import('./pages/docs/PrivacyScheduler'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const CaseStudies = lazy(() => import('./pages/CaseStudies'));
const GetQuote = lazy(() => import('./pages/GetQuote'));
const About = lazy(() => import('./pages/About'));
const SalesPage = lazy(() => import('./pages/SalesPage'));
const Blogs = lazy(() => import('./pages/Blogs'));
const BlogSingle = lazy(() => import('./pages/BlogSingle'));
const Members = lazy(() => import('./pages/Members'));
const Converters = lazy(() => import('./pages/Converters'));
const Community = lazy(() => import('./pages/Community'));
const FloppyCardDemo = lazy(() => import('./pages/FloppyCardDemo'));
const AuthCallback = lazy(() => import('./components/AuthCallback'));

// Lazy load tools
const ContrastChecker = lazy(() => import('./pages/tools/ContrastChecker'));
const AspectRatioCalculator = lazy(() => import('./pages/tools/AspectRatioCalculator'));
const SVGtoCSS = lazy(() => import('./pages/tools/SVGtoCSS'));
const CSSShadowGenerator = lazy(() => import('./pages/tools/CSSShadowGenerator'));
const LoremGenerator = lazy(() => import('./pages/tools/LoremGenerator'));
const UnitConverter = lazy(() => import('./pages/tools/UnitConverter'));
const Base64EncodeDecode = lazy(() => import('./pages/tools/Base64EncodeDecode'));
const MarkdownEditor = lazy(() => import('./pages/tools/MarkdownEditor'));
const ColorFromImage = lazy(() => import('./pages/tools/ColorFromImage'));
const CSSClipPathGenerator = lazy(() => import('./pages/tools/CSSClipPathGenerator'));
const ImageConverter = lazy(() => import('./pages/tools/ImageConverter'));
const FaviconGenerator = lazy(() => import('./pages/tools/FaviconGenerator'));
const ImageOptimizer = lazy(() => import('./pages/tools/ImageOptimizer'));
const SVGOptimizer = lazy(() => import('./pages/tools/SVGOptimizer'));
const CodeMinifier = lazy(() => import('./pages/tools/CodeMinifier'));

// Loading fallback
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-10 h-10 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin"></div>
  </div>
);

function App() {
  const queryClient = new QueryClient();

  useEffect(() => {
    initializeConsent();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <Router>
          <ScrollToTop />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<Layout><Home /></Layout>} />
              <Route path="/tools" element={<Layout><Tools /></Layout>} />
              <Route path="/tools/contrast-checker" element={<Layout><ContrastChecker /></Layout>} />
              <Route path="/tools/lorem-generator" element={<Layout><LoremGenerator /></Layout>} />
              <Route path="/tools/unit-converter" element={<Layout><UnitConverter /></Layout>} />
              <Route path="/tools/base64" element={<Layout><Base64EncodeDecode /></Layout>} />
              <Route path="/tools/aspect-ratio-calculator" element={<Layout><AspectRatioCalculator /></Layout>} />
              <Route path="/tools/css-shadow-generator" element={<Layout><CSSShadowGenerator /></Layout>} />
              <Route path="/tools/svg-to-css" element={<Layout><SVGtoCSS /></Layout>} />
              <Route path="/tools/markdown-editor" element={<Layout><MarkdownEditor /></Layout>} />
              <Route path="/tools/color-from-image" element={<Layout><ColorFromImage /></Layout>} />
              <Route path="/tools/css-clip-path-generator" element={<Layout><CSSClipPathGenerator /></Layout>} />
              <Route path="/tools/image-converter" element={<Layout><ImageConverter /></Layout>} />
              <Route path="/tools/favicon-generator" element={<Layout><FaviconGenerator /></Layout>} />
              <Route path="/tools/image-optimizer" element={<Layout><ImageOptimizer /></Layout>} />
              <Route path="/tools/svg-optimizer" element={<Layout><SVGOptimizer /></Layout>} />
              <Route path="/tools/code-minifier" element={<Layout><CodeMinifier /></Layout>} />
              <Route path="/community" element={<Layout><Community /></Layout>} />
              <Route path="/marketplace" element={<Layout><Marketplace /></Layout>} />
              <Route path="/blog" element={<Layout><Blogs /></Layout>} />
              <Route path="/blog/:url" element={<Layout><BlogSingle /></Layout>} />
              <Route path="/docs/terms-scheduler" element={<Layout><TermsScheduler /></Layout>} />
              <Route path="/docs/privacy-scheduler" element={<Layout><PrivacyScheduler /></Layout>} />
              <Route path="/privacy-policy" element={<Layout><PrivacyPolicy /></Layout>} />
              <Route path="/cookie-policy" element={<Layout><CookiePolicy /></Layout>} />
              <Route path="/members" element={<Layout isFullScreen><Members /></Layout>} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/converters" element={<Layout><Converters /></Layout>} />
              <Route path="/product/:link" element={<Layout><ProductDetails /></Layout>} />
              <Route path="/case-studies" element={<Layout><CaseStudies /></Layout>} />
              <Route path="/get-quote" element={<Layout><GetQuote /></Layout>} />
              <Route path="/sales" element={<Layout><SalesPage /></Layout>} />
              <Route path="/about" element={<Layout><About /></Layout>} />
              <Route path="/contact" element={<Layout><Contact /></Layout>} />
              <Route path="/pricing" element={<Layout><PricingPage /></Layout>} />
              <Route path="/floppy-demo" element={<Layout><FloppyCardDemo /></Layout>} />
              <Route path="*" element={<Custom404 />} />
            </Routes>
          </Suspense>
          <CookieConsent />
          {/* <ConsentDebug /> */}
        </Router>
      </HelmetProvider>
    </QueryClientProvider>
  );
}

export default App;
